import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  afterEveryRender,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { CapitalizePipe } from '@hmax-order/frontend/utils';

/**
 * Chips de uma coluna `TAGS` da tabela, recolhidos em **UMA linha**, com "+N exibir mais" no que
 * não coube. Responsivo: se cabem três, mostra três; se a coluna encolhe, mostra menos.
 *
 * 🔴 **Quem decide o corte é o layout do navegador, não uma conta em JS.** As duas versões
 * anteriores somavam largura de chip + `gap` e comparavam com `clientWidth`; erravam por baixo e
 * exibiam um chip só com meia coluna vazia ao lado. Aqui os chips ficam **todos** no DOM, o
 * `flex-wrap` empurra o que não cabe para a segunda linha e o `max-h-8` a esconde. O JS só lê
 * `offsetTop` para contar quem caiu fora — nenhuma largura, nenhum `gap`, nenhuma estimativa.
 *
 * 🔴 **Nenhum chip é escondido com `hidden`.** Chip escondido mede zero: a conta seguinte concluiria
 * que tudo cabe, mostraria todos de novo e voltaria a esconder — a oscilação que derrubou a primeira
 * versão. Cortar por `overflow` mantém a medida sempre válida.
 *
 * Se o JS não rodar, o pior caso é ficar sem o "+N": a linha continua com altura de uma linha e
 * nada estoura a coluna.
 */
@Component({
  selector: 'hmax-table-tags',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, CapitalizePipe],
  templateUrl: './table-tags.component.html',
  host: { class: 'block w-full min-w-0' },
})
export class TableTagsComponent {
  readonly tags = input.required<string[]>();
  /** Traduz cada valor pela chave do i18n (papéis usam a chave em MAIÚSCULAS). */
  readonly translate = input<boolean>(false);

  private readonly _container = viewChild<ElementRef<HTMLElement>>('container');
  private readonly _chips = viewChildren<ElementRef<HTMLElement>>('chip');
  private readonly _toggleButton = viewChild<ElementRef<HTMLElement>>('toggleButton');

  private readonly _document = inject(DOCUMENT);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _ngZone = inject(NgZone);

  protected readonly expanded = signal(false);
  protected readonly hiddenCount = signal(0);

  protected readonly showToggle = computed(() => this.expanded() || this.hiddenCount() > 0);
  /** Recolhido, o botão entra entre os dois grupos; expandido, todos os chips vêm antes dele. */
  protected readonly visibleTags = computed(() =>
    this.expanded() ? this.tags() : this.tags().slice(0, this.tags().length - this.hiddenCount()),
  );
  protected readonly hiddenTags = computed(() =>
    this.expanded() ? [] : this.tags().slice(this.tags().length - this.hiddenCount()),
  );

  constructor() {
    // Lista nova: volta ao estado inicial e deixa a contagem decidir de novo.
    effect(() => {
      this.tags();
      this.hiddenCount.set(0);
    });

    // A contagem refaz a cada render, na fase de leitura (layout já pronto). É uma leitura de
    // `offsetTop` por chip e os `set` só notificam quando o valor muda, então a sequência converge
    // em vez de reentrar.
    afterEveryRender({ read: () => this.recount() });

    // A tabela costuma assentar a largura das colunas DEPOIS do primeiro paint: a primeira contagem
    // sai da coluna no `min-width` e daria "+3" com meia coluna vazia. Estes reforços cobrem o caso
    // em que nada mais dispara change detection nem resize entre o layout provisório e o final.
    afterNextRender(() => {
      const view = this._document.defaultView;
      view?.requestAnimationFrame(() => this._ngZone.run(() => this.recount()));
      const timer = view?.setTimeout(() => this._ngZone.run(() => this.recount()), 300);
      this._destroyRef.onDestroy(() => view?.clearTimeout(timer));
    });

    const view = this._document.defaultView;
    if (view?.ResizeObserver) {
      // Resize da coluna que não passa por change detection — abrir o menu lateral, por exemplo.
      // `run` porque o callback nasce fora da zona: sem ele o valor novo fica no signal e a tela só
      // atualiza no próximo clique.
      const observer = new view.ResizeObserver(() => this._ngZone.run(() => this.recount()));
      effect(() => {
        const container = this._container()?.nativeElement;
        observer.disconnect();
        if (container) {
          observer.observe(container);
        }
      });
      this._destroyRef.onDestroy(() => observer.disconnect());
    }
  }

  protected label(tag: string): string {
    return this.translate() ? this._translocoService.translate(tag) : tag;
  }

  protected toggleExpanded(event: Event): void {
    event.stopPropagation();
    this.expanded.update((value) => !value);
  }

  /** Conta os chips que o navegador empurrou para fora da primeira linha. */
  private recount(): void {
    if (this.expanded()) {
      return;
    }

    const chips = this._chips();
    if (!chips.length) {
      this.hiddenCount.set(0);
      return;
    }

    const firstLine = chips[0].nativeElement.offsetTop;
    const hidden = chips.filter((chip) => chip.nativeElement.offsetTop > firstLine).length;

    // O botão ocupa lugar na linha como qualquer chip: se ele mesmo sobrou, recolhe mais um chip
    // para abrir espaço. Converge, não oscila — o "+N" só some quando tudo cabe SEM ele, e nesse
    // caso ele não é renderizado para tirar espaço de ninguém.
    const toggleOverflowed = (this._toggleButton()?.nativeElement.offsetTop ?? firstLine) > firstLine;
    this.hiddenCount.set(hidden + (toggleOverflowed ? 1 : 0));
  }
}
